/* eslint-disable react-refresh/only-export-components */
import type { Clerk } from '@clerk/types'
import { useEffect, useRef, type ComponentType } from 'react'
import { createBrowserRouter, Outlet, redirect, useLocation, type MiddlewareFunction } from 'react-router'
import { Userpilot } from 'userpilot'

import { DatabaseUserGuard } from '@/components/DatabaseUserGuard'
import { MandatoryAssessmentGuard } from '@/components/guards/MandatoryAssessmentGuard'
import { NeedsAnalysisGuard } from '@/components/guards/NeedsAnalysisGuard'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { OrgAccessGuard } from '@/components/guards/OrgAccessGuard'
import { FeatureFlagGuard } from '@/components/guards/FeatureFlagGuard'
import { RootErrorBoundary } from '@/components/layout/RootErrorBoundary'
import { DocumentTitle } from '@/components/utils/DocumentTitle'
import { Toaster } from '@/components/ui/sonner'

import { ClerkUserRole } from '@/types/auth'
import { FeatureAccessPath } from '@/types/configuration'

import { BulkAddUsersProvider } from '@/contexts/BulkAddUsersProvider'
import clerkContext from '@/contexts/routerClerkContext.ts'
import { SessionHeartbeatProvider } from '@/modules/time-on-session/provider/SessionHeartbeatProvider'
import { useHighContrastMode } from '@/hooks/useHighContrastMode'
import { useTrialAcAutoRefresh } from '@/hooks/useTrialAcAutoRefresh'
import { useUserpilotIdentify } from '@/hooks/useUserpilotIdentify'

declare global {
  interface Window {
    Clerk?: Clerk
  }
}

const authMiddleware: MiddlewareFunction = async ({ request, context }) => {
  while (!window.Clerk?.loaded) {
    await new Promise((resolve) => setTimeout(resolve, 100))
  }

  if (!window.Clerk.user) {
    throw redirect(`/signin?return_to=${new URL(request.url).pathname}`)
  }

  context.set(clerkContext, window.Clerk)
}

const mapDefault = async (importFn: Promise<{ default: ComponentType }>) => {
  const { default: Component, ...module } = await importFn
  return {
    ...module,
    Component,
  }
}

const router = createBrowserRouter([
  {
    element: <Root />,
    errorElement: <RootErrorBoundary />,
    loader: () => {}, // Dummy loader so middleware runs
    children: [
      { path: '/', lazy: () => mapDefault(import('./routes/Home.tsx')) },

      /* Writing Workshop */
      {
        middleware: [authMiddleware],
        element: (
          <ProtectedRoute
            allowedRoles={[
              ClerkUserRole.SUPER_ADMIN,
              ClerkUserRole.ENGEN_ADMIN,
              ClerkUserRole.CLIENT_ADMIN,
              ClerkUserRole.LEARNER,
              ClerkUserRole.INSTRUCTOR,
              ClerkUserRole.COACH,
              ClerkUserRole.CURRICULUM_DESIGNER,
            ]}
          >
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          {
            path: '/writing',
            lazy: () => mapDefault(import('./routes/WritingHelper.tsx')),
          },
        ],
      },

      /* Courses & Lessons */
      {
        path: '/courses',
        middleware: [authMiddleware],
        element: (
          <ProtectedRoute>
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          { index: true, lazy: () => mapDefault(import('./routes/Courses')) },
          {
            path: ':courseId',
            lazy: () => mapDefault(import('./components/courses/CourseDetailPage')),
          },
          {
            path: ':courseId/lessons/:lesson_sanity_id',
            lazy: async () => mapDefault(import('./routes/lessons/LessonPlayer')),
          },
        ],
      },
      {
        path: '/lessons',
        middleware: [authMiddleware],
        element: (
          <ProtectedRoute>
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          { index: true, lazy: () => mapDefault(import('./routes/Lessons')) },
          {
            path: ':lesson_sanity_id',
            lazy: async () => mapDefault(import('./routes/lessons/LessonPlayer')),
          },
          {
            path: 'unit-summary/:unit_sanity_id',
            lazy: async () => mapDefault(import('./routes/lessons/UnitSummaryReport')),
          },
        ],
      },

      {
        path: '/live-sessions',
        loader: () => redirect('/group-classes'),
      },
      {
        path: '/group-classes',
        middleware: [authMiddleware],
        element: (
          <ProtectedRoute>
            <OrgAccessGuard
              configPath={FeatureAccessPath.LIVE_SESSIONS_GROUP_CLASSES_ENABLED}
              restrictedRoles={[ClerkUserRole.LEARNER, ClerkUserRole.COACH]}
            >
              <Outlet />
            </OrgAccessGuard>
          </ProtectedRoute>
        ),
        children: [
          { index: true, lazy: () => mapDefault(import('./routes/GroupClasses')) },
          {
            path: ':liveSessionId',
            lazy: () => mapDefault(import('./components/live-sessions/video-call/VideoCalling')),
          },
        ],
      },
      {
        path: '/private-classes',
        middleware: [authMiddleware],
        element: (
          <ProtectedRoute>
            <OrgAccessGuard
              configPath={FeatureAccessPath.LIVE_SESSIONS_PRIVATE_CLASSES_ENABLED}
              restrictedRoles={[ClerkUserRole.LEARNER]}
            >
              <Outlet />
            </OrgAccessGuard>
          </ProtectedRoute>
        ),
        children: [
          { index: true, lazy: () => mapDefault(import('./routes/LearnerPrivateClasses')) },
          {
            path: ':liveSessionId',
            lazy: () => mapDefault(import('./components/live-sessions/video-call/VideoCalling')),
          },
        ],
      },

      { path: '/dashboard', loader: () => redirect('/admin') },
      { path: '/dashboard/*', loader: () => redirect('/admin') },

      { path: '/video-calling', lazy: () => mapDefault(import('./routes/VideoCalling')) },
      { path: '/video-calling/:callId', lazy: () => mapDefault(import('./routes/VideoCalling')) },

      // Standalone, header-less Zoom Client View — a top-level document
      // (not an iframe) so it can be cross-origin isolated for gallery view.
      // The dispatcher hard-navigates here; this page owns the full viewport.
      // Auth-protected like the rest of the app — same-origin cookies
      // mean the page sees Clerk's session without any handshake.
      //
      // Permission to actually JOIN the session is enforced server-side by
      // ``POST /live-sessions/:id/join``; the role gate here just keeps
      // unauthenticated users off the page.
      {
        path: '/zoom-frame/:liveSessionId',
        middleware: [authMiddleware],
        element: (
          <ProtectedRoute
            allowedRoles={[
              ClerkUserRole.SUPER_ADMIN,
              ClerkUserRole.ENGEN_ADMIN,
              ClerkUserRole.CLIENT_ADMIN,
              ClerkUserRole.INSTRUCTOR,
              ClerkUserRole.LEARNER,
              ClerkUserRole.COACH,
              ClerkUserRole.CURRICULUM_DESIGNER,
            ]}
          >
            <Outlet />
          </ProtectedRoute>
        ),
        children: [{ index: true, lazy: () => mapDefault(import('./routes/ZoomFrame.tsx')) }],
      },

      {
        path: '/word-bank',
        middleware: [authMiddleware],
        element: (
          <ProtectedRoute
            allowedRoles={[
              ClerkUserRole.SUPER_ADMIN,
              ClerkUserRole.ENGEN_ADMIN,
              ClerkUserRole.CLIENT_ADMIN,
              ClerkUserRole.LEARNER,
              ClerkUserRole.INSTRUCTOR,
              ClerkUserRole.COACH,
              ClerkUserRole.CURRICULUM_DESIGNER,
            ]}
          >
            <FeatureFlagGuard flagName='word-bank-access'>
              <Outlet />
            </FeatureFlagGuard>
          </ProtectedRoute>
        ),
        children: [
          {
            path: '',
            lazy: () => mapDefault(import('./routes/WordBank')),
            children: [
              { path: 'activity', lazy: () => mapDefault(import('./components/wordbank/ActivityPlayer.tsx')) },
            ],
          },
        ],
      },

      {
        path: '/grammar',
        middleware: [authMiddleware],
        element: (
          <ProtectedRoute>
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          { index: true, lazy: () => mapDefault(import('./routes/GrammarGuide')) },
          { path: ':categorySlug', lazy: () => mapDefault(import('./routes/GrammarGuide')) },
        ],
      },

      {
        path: '/performance',
        middleware: [authMiddleware],
        element: (
          <ProtectedRoute
            allowedRoles={[
              ClerkUserRole.SUPER_ADMIN,
              ClerkUserRole.ENGEN_ADMIN,
              ClerkUserRole.CLIENT_ADMIN,
              ClerkUserRole.LEARNER,
              ClerkUserRole.INSTRUCTOR,
              ClerkUserRole.COACH,
              ClerkUserRole.CURRICULUM_DESIGNER,
            ]}
          >
            <Outlet />
          </ProtectedRoute>
        ),
        children: [{ index: true, lazy: () => mapDefault(import('./routes/Performance')) }],
      },

      {
        path: '/history',
        middleware: [authMiddleware],
        element: (
          <ProtectedRoute
            allowedRoles={[
              ClerkUserRole.SUPER_ADMIN,
              ClerkUserRole.ENGEN_ADMIN,
              ClerkUserRole.CLIENT_ADMIN,
              ClerkUserRole.LEARNER,
              ClerkUserRole.INSTRUCTOR,
              ClerkUserRole.COACH,
              ClerkUserRole.CURRICULUM_DESIGNER,
            ]}
          >
            <Outlet />
          </ProtectedRoute>
        ),
        children: [{ index: true, lazy: () => mapDefault(import('./routes/LearnerHistory.tsx')) }],
      },

      {
        // A learner's coaching conversation (ENG-3375). Its own top-level route
        // rather than a tab under something else: it is where an email link
        // lands, so the URL has to be short, stable and shareable.
        path: '/coaching',
        middleware: [authMiddleware],
        element: (
          <ProtectedRoute allowedRoles={[ClerkUserRole.SUPER_ADMIN, ClerkUserRole.ENGEN_ADMIN, ClerkUserRole.LEARNER]}>
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          { index: true, lazy: () => mapDefault(import('./routes/Coaching.tsx')) },
          { path: 'messages', lazy: () => mapDefault(import('./routes/CoachingMessages.tsx')) },
          // Booking, and moving a booking, are the same screen (ENG-3351): both
          // ask which of the coach's open times, and `?move=<id>` is the only
          // difference. Not in the header menu — the coaching home links to it.
          { path: 'book', lazy: () => mapDefault(import('./routes/CoachingBook.tsx')) },
          // Every coach a learner could book, with the detail panel as `?coach=`
          // rather than a nested route so closing it keeps the list's scroll
          // position and filters (S17).
          { path: 'coaches', lazy: () => mapDefault(import('./routes/CoachingCoaches.tsx')) },
          // The meeting room itself, reusing the component group and private
          // classes already join through. Its own path under coaching so the
          // learner-coaching guard applies and the breadcrumb stays coherent.
          {
            path: 'meetings/:liveSessionId',
            lazy: () => mapDefault(import('./routes/CoachingMeeting.tsx')),
          },
        ],
      },

      {
        path: '/learning-time',
        middleware: [authMiddleware],
        element: (
          <ProtectedRoute
            allowedRoles={[
              ClerkUserRole.SUPER_ADMIN,
              ClerkUserRole.ENGEN_ADMIN,
              ClerkUserRole.CLIENT_ADMIN,
              ClerkUserRole.LEARNER,
              ClerkUserRole.INSTRUCTOR,
              ClerkUserRole.COACH,
              ClerkUserRole.CURRICULUM_DESIGNER,
            ]}
          >
            <FeatureFlagGuard flagName='show-learning-time'>
              <Outlet />
            </FeatureFlagGuard>
          </ProtectedRoute>
        ),
        children: [{ index: true, lazy: () => mapDefault(import('./routes/LearningTime.tsx')) }],
      },

      {
        path: '/admin',
        middleware: [authMiddleware],
        element: (
          <ProtectedRoute>
            <BulkAddUsersProvider>
              <Outlet />
            </BulkAddUsersProvider>
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            lazy: () => mapDefault(import('./routes/admin/Dashboard.tsx')),
          },
          {
            path: 'my-courses',
            lazy: () => mapDefault(import('./routes/admin/MyCourses')),
          },
          {
            path: 'users',
            lazy: () => mapDefault(import('./routes/admin/UserManagement')),
          },
          {
            path: 'performance-history/:userId',
            lazy: () => mapDefault(import('./routes/admin/UserPerformanceHistory')),
          },
          {
            path: 'my-organization',
            lazy: () => mapDefault(import('./routes/admin/MyOrganization')),
          },
          {
            path: 'group-classes',
            children: [
              {
                index: true,
                lazy: () => mapDefault(import('./routes/admin/LiveSessions')),
              },
              {
                path: ':liveSessionId',
                lazy: () => mapDefault(import('./components/live-sessions/video-call/VideoCalling')),
              },
            ],
          },
          {
            path: 'private-classes',
            children: [
              {
                index: true,
                lazy: () => mapDefault(import('./routes/PrivateClasses')),
                handle: { title: 'Private Classes' },
              },
              {
                path: ':liveSessionId',
                lazy: () => mapDefault(import('./components/live-sessions/video-call/VideoCalling')),
                handle: { title: 'Private Class' },
              },
            ],
          },
          {
            path: 'my-availability',
            lazy: () => mapDefault(import('./routes/admin/MyAvailability')),
          },
          {
            path: 'my-learners',
            children: [
              {
                index: true,
                lazy: () => mapDefault(import('./routes/admin/MyLearners')),
              },
              {
                path: ':learnerUserId',
                lazy: () => mapDefault(import('./routes/admin/MyLearnerDetail')),
              },
            ],
          },
          {
            path: 'my-messages',
            lazy: () => mapDefault(import('./routes/admin/CoachMessages')),
          },
          {
            path: 'my-profile',
            lazy: () => mapDefault(import('./routes/admin/MyProfile')),
          },
          {
            path: 'content',
            lazy: () => mapDefault(import('./routes/admin/CoachContent')),
          },
          {
            path: 'coaching',
            children: [
              {
                index: true,
                loader: () => redirect('/admin/coaching/staffing'),
              },
              {
                path: 'staffing',
                lazy: () => mapDefault(import('./routes/admin/Staffing')),
              },
              {
                path: 'assignments',
                lazy: () => mapDefault(import('./routes/admin/CoachAssignments')),
              },
              {
                path: 'other-coach-meetings',
                lazy: () => mapDefault(import('./routes/admin/OtherCoachMeetings')),
              },
              {
                path: 'missed-messages',
                lazy: () => mapDefault(import('./routes/admin/MissedMessages')),
              },
              {
                path: 'coach-inbox',
                lazy: () => mapDefault(import('./routes/admin/CoachInbox')),
              },
              {
                path: 'coaches/:coachUserId',
                loader: ({ params }) => redirect(`/admin/coaching/coaches/${params.coachUserId}/meetings`),
              },
              {
                path: 'coaches/:coachUserId/learners',
                lazy: () => mapDefault(import('./routes/admin/CoachLearners')),
              },
              {
                path: 'coaches/:coachUserId/availability',
                lazy: () => mapDefault(import('./routes/admin/CoachAvailability')),
              },
              {
                path: 'coaches/:coachUserId/meetings',
                lazy: () => mapDefault(import('./routes/admin/CoachMeetings')),
              },
              {
                path: 'meetings/:liveSessionId',
                lazy: () => mapDefault(import('./routes/admin/CoachingMeeting')),
              },
            ],
          },
          {
            path: 'reporting',
            lazy: () => mapDefault(import('./routes/admin/Reporting')),
          },
          {
            path: 'organizations',
            lazy: () => mapDefault(import('./routes/admin/Organizations')),
          },
          {
            path: 'organizations/:orgSlug',
            children: [
              {
                index: true,
                lazy: () => mapDefault(import('./routes/admin/OrganizationDetail')),
              },
              {
                path: ':tab',
                lazy: () => mapDefault(import('./routes/admin/OrganizationDetail')),
              },
            ],
          },
          {
            path: 'users/:userId',
            lazy: () => mapDefault(import('./routes/admin/UserDetail')),
          },
          {
            path: 'system-settings',
            lazy: () => mapDefault(import('./routes/admin/SystemSettings')),
          },
          {
            path: 'pa-scoring-configs',
            lazy: () => mapDefault(import('./routes/admin/PAScoringConfigs')),
          },
          {
            path: 'trials',
            children: [
              {
                index: true,
                lazy: () => mapDefault(import('./routes/admin/Trials')),
              },
              {
                path: ':enrollmentId',
                lazy: () => mapDefault(import('./routes/admin/Trials')),
              },
            ],
          },
          {
            path: 'writing-helper',
            lazy: () => mapDefault(import('./routes/admin/WritingHelperAdmin')),
          },
          {
            path: 'assessment',
            lazy: () => mapDefault(import('./routes/admin/AssessmentLogsAdmin')),
          },
          {
            path: 'courses',
            children: [
              {
                index: true,
                lazy: () => mapDefault(import('./routes/admin/Courses')),
              },
              {
                path: ':slug',
                lazy: () => mapDefault(import('./routes/admin/Course')),
              },
            ],
          },
          {
            path: 'assignments',
            lazy: () => mapDefault(import('./routes/admin/Assignments')),
          },
          {
            path: 'lessons',
            children: [
              {
                index: true,
                lazy: () => mapDefault(import('./routes/admin/LessonsSearch')),
              },
              {
                path: 'article/:id',
                lazy: () => mapDefault(import('./routes/admin/lessons/ArticleLesson')),
              },
              {
                path: 'video/:id',
                lazy: () => mapDefault(import('./routes/admin/lessons/VideoLesson')),
              },
              {
                path: 'audio/:id',
                lazy: () => mapDefault(import('./routes/admin/lessons/AudioLesson')),
              },
              {
                path: 'image/:id',
                lazy: () => mapDefault(import('./routes/admin/lessons/ImageLesson')),
              },
            ],
          },
          {
            path: 'headwords',
            children: [
              {
                index: true,
                lazy: () => mapDefault(import('./routes/admin/Headwords')),
              },
              {
                path: ':headwordSlug',
                lazy: () => mapDefault(import('./routes/admin/Headwords')),
              },
            ],
          },
        ],
      },

      /* Export Download (email link handler) */
      {
        path: '/exports/:exportId/download',
        middleware: [authMiddleware],
        element: (
          <ProtectedRoute
            allowedRoles={[ClerkUserRole.SUPER_ADMIN, ClerkUserRole.ENGEN_ADMIN, ClerkUserRole.CLIENT_ADMIN]}
          >
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            lazy: () => mapDefault(import('./routes/exports/ExportDownloadPage')),
          },
        ],
      },

      /* Standalone trial intake simulator (EnGen/super admin, learner-styled, no admin chrome).
         Access stays double-gated: the trial-intake-simulator Unleash flag is the real
         control, so an admin reaches the page only if they also hold the flag (ENG-2979). */
      {
        path: '/trial-intake-simulator',
        middleware: [authMiddleware],
        element: (
          <ProtectedRoute allowedRoles={[ClerkUserRole.SUPER_ADMIN, ClerkUserRole.ENGEN_ADMIN]}>
            <Outlet />
          </ProtectedRoute>
        ),
        children: [{ index: true, lazy: () => mapDefault(import('./routes/TrialIntakeSimulatorPage')) }],
      },

      /* Automated comms timeline simulator (ENG-3784): super admin and the automated-comms-simulator flag. */
      {
        path: '/automated-comms-simulator',
        middleware: [authMiddleware],
        element: (
          <ProtectedRoute allowedRoles={[ClerkUserRole.SUPER_ADMIN]}>
            <Outlet />
          </ProtectedRoute>
        ),
        children: [{ index: true, lazy: () => mapDefault(import('./routes/AutomatedCommsSimulatorPage')) }],
      },

      {
        path: '/instructor',
        middleware: [authMiddleware],
        element: (
          <ProtectedRoute allowedRoles={[ClerkUserRole.INSTRUCTOR, ClerkUserRole.SUPER_ADMIN]}>
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          { path: 'learner-assessments', lazy: () => mapDefault(import('./routes/instructor/LearnerAssessments')) },
        ],
      },

      { path: '/signin', lazy: () => mapDefault(import('./routes/SignInPage')) },
      { path: '/accept-invite/:token', lazy: () => mapDefault(import('./routes/AcceptInvitePage')) },
      { path: '/reset-password/:token', lazy: () => mapDefault(import('./routes/ResetPasswordPage')) },
      { path: '/auth/sso/:name', lazy: () => mapDefault(import('./routes/SSOCallbackPage')) },
      { path: '/auth/sso-callback', lazy: () => mapDefault(import('./routes/ClerkSSOCallback')) },
      {
        path: '/create-password',
        middleware: [authMiddleware],
        lazy: () => mapDefault(import('./routes/CreatePasswordPage')),
      },
      { path: '/auth/error', lazy: () => mapDefault(import('./routes/AuthError')) },
      { path: '/lti/callback', lazy: () => mapDefault(import('./routes/LTICallback')) },

      {
        path: '/terms-of-use',
        lazy: () => mapDefault(import('./routes/TermsPage')),
      },
      {
        path: '/privacy-policy',
        lazy: () => mapDefault(import('./routes/TermsPage')),
      },
      {
        path: '/accessibility',
        lazy: () => mapDefault(import('./routes/TermsPage')),
      },
    ],
  },
])

function UserpilotReloader() {
  const location = useLocation()
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null)

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      Userpilot.reload()
    }, 300)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [location])

  return null
}

const routerSubscribe = router.subscribe.bind(router)

function Root() {
  useHighContrastMode()
  // Userpilot identify + the trial AC refresh live here, not in Header, so they
  // keep running on full-screen flows that unmount Header (assessment runner,
  // lesson player) where a trial learner's time-on-task crossing often happens.
  // Root is mounted for the whole authenticated session (ENG-3128).
  useUserpilotIdentify()
  useTrialAcAutoRefresh()

  return (
    <>
      <DatabaseUserGuard>
        <SessionHeartbeatProvider routerSubscribe={routerSubscribe}>
          <NeedsAnalysisGuard>
            <MandatoryAssessmentGuard>
              <Outlet />
            </MandatoryAssessmentGuard>
          </NeedsAnalysisGuard>
        </SessionHeartbeatProvider>
      </DatabaseUserGuard>

      <UserpilotReloader />
      <DocumentTitle />
      <Toaster richColors />
    </>
  )
}

export default router
